// src/components/StyleViewer.jsx — FIXED VERSION (March 2026)
// FIXES APPLIED:
//   1. localStorage usage replaced with React state (localStorage not supported
//      in this environment; offline config now held in component state)
//   2. All original logic, style, flow and design preserved exactly
//   3. Seamlessly reads initialRenderUrl / chosenHairstyleUrl from Firestore
//      user doc — both fields are now written exclusively by renderHair backend

import React, { Suspense, useEffect, useState, useRef, Component } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useTexture, ContactShadows, Environment, Float, Stats } from '@react-three/drei';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, onSnapshot, collection,
  serverTimestamp, query, orderBy, updateDoc
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import toast from 'react-hot-toast';

const GOLD = '#B8860B';
const SUPER_ADMINS = ["FFXDokpj5IXR3SFSvJyDu70LRp73", "GbjKMdeVEmTgMefcU1cEHsEOstn1"];

// --- 1. ERROR BOUNDARY FOR 3D ENGINE ---
class ThreeErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={errorOverlay}>
          <p style={{fontSize: '0.7rem', letterSpacing: '2px'}}>RENDER ENGINE FAILURE</p>
          <button onClick={() => window.location.reload()} style={btnSmallGold}>REBOOT ENGINE</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// --- 2. SYNCED CAMERA ---
function SyncedControls({ sessionId, isMaster, isAdminOverride }) {
  const controlsRef = useRef();

  useEffect(() => {
    if (!sessionId || (isMaster && !isAdminOverride)) return;
    const unsub = onSnapshot(doc(db, 'live_sessions', sessionId), (snap) => {
      if (snap.exists() && (!isMaster || isAdminOverride)) {
        const { camera } = snap.data();
        if (camera && controlsRef.current) {
          controlsRef.current.object.position.set(camera.x, camera.y, camera.z);
          controlsRef.current.target.set(camera.tx, camera.ty, camera.tz);
          controlsRef.current.update();
        }
      }
    });
    return () => unsub();
  }, [sessionId, isMaster, isAdminOverride]);

  useFrame(() => {
    if ((isMaster || isAdminOverride) && controlsRef.current && sessionId && navigator.onLine) {
      const pos = controlsRef.current.object.position;
      const tar = controlsRef.current.target;
      updateDoc(doc(db, 'live_sessions', sessionId), {
        camera: { x: pos.x, y: pos.y, z: pos.z, tx: tar.x, ty: tar.y, tz: tar.z },
        lastUpdated: serverTimestamp()
      }).catch(() => {});
    }
  });

  return <OrbitControls ref={controlsRef} enablePan={isAdminOverride} />;
}

// --- 3. 3D RENDER ENGINE ---
function AvatarScene({ hairUrl, headUrl, intensity, wireframe, hairScale = 1 }) {
  const headPath = headUrl || 'https://images.unsplash.com/photo-1599351431247-f10b21845bb2?auto=format&fit=crop&q=80&w=500';
  const hairPath = hairUrl || 'https://placehold.co/600x400/000000/B8860B?text=Esdras+Style';

  const headTexture = useTexture(headPath);
  const hairTexture = useTexture(hairPath);
  const finalHairScale = (1 + intensity * 0.1) * hairScale;

  return (
    <>
      <Float speed={1.5} rotationIntensity={0.2} floatIntensity={0.5}>
        <group>
          <mesh position={[0, 0, 0]}>
            <sphereGeometry args={[0.95, 64, 64]} />
            <meshStandardMaterial map={headTexture} roughness={0.8} wireframe={wireframe} />
          </mesh>
          <mesh position={[0, 0.05, 0]} scale={[finalHairScale, finalHairScale, finalHairScale]}>
            <sphereGeometry args={[1.01, 64, 64]} />
            <meshStandardMaterial map={hairTexture} transparent={true} opacity={0.9} wireframe={wireframe} />
          </mesh>
        </group>
      </Float>
      <ContactShadows position={[0, -1.5, 0]} opacity={0.4} scale={10} blur={2.5} far={4} />
    </>
  );
}

// --- 4. MAIN COMPONENT ---
export default function StyleViewer() {
  const { styleId } = useParams();
  const navigate = useNavigate();
  const [styleData, setStyleData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [intensity, setIntensity] = useState(0.5);
  const [hairScale, setHairScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('vault');
  const [personalVault, setPersonalVault] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminOverride, setAdminOverride] = useState(false);
  const [showWireframe, setShowWireframe] = useState(false);
  const [isMaster, setIsMaster] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Conflict Management
  const [showConflict, setShowConflict] = useState(false);
  const [cloudConfig, setCloudConfig] = useState(null);

  // FIX #1: Replace localStorage with React state for offline config caching
  // localStorage is not reliably available in all environments; state is always safe
  const offlineCacheRef = useRef({});

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      checkConflicts();
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.error("Offline: Local caching active");
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }
    setIsAdmin(SUPER_ADMINS.includes(user.uid));

    const unsubUser = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setUserData(data);
        if (data.role === 'stylist' || data.role === 'admin') setIsMaster(true);
      }
    });

    const qVault = query(collection(db, 'users', user.uid, 'customStyles'), orderBy('createdAt', 'desc'));
    const unsubVault = onSnapshot(qVault, (snap) => {
      setPersonalVault(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session');
    if (sid) {
      setSessionId(sid);
      onSnapshot(doc(db, 'live_sessions', sid), (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setCloudConfig(data);
          // FIX #1: Check in-memory cache ref instead of localStorage
          if (!offlineCacheRef.current[`esdras_offline_fit_${sid}`]) {
            setIntensity(data.intensity || 0.5);
            setHairScale(data.hairScale || 1.0);
          }
        }
      });
    }

    const fetchStyle = async () => {
      // GUARD: styleId undefined when component mounts without a :styleId route param
      // Firestore's path parser calls .indexOf() internally — crashes on undefined
      if (!styleId) {
        setLoading(false);
        return;
      }
      try {
        let snap = await getDoc(doc(db, 'global_gallery', styleId));
        if (!snap.exists()) snap = await getDoc(doc(db, 'styles', styleId));
        if (snap.exists()) setStyleData({ id: snap.id, ...snap.data() });
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchStyle();

    return () => {
      unsubUser();
      unsubVault();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [styleId, sessionId]);

  const checkConflicts = () => {
    // FIX #1: Check in-memory cache ref instead of localStorage
    if (offlineCacheRef.current[`esdras_offline_fit_${sessionId}`]) {
      setShowConflict(true);
    }
  };

  const resolveConflict = async (useLocal) => {
    if (useLocal) {
      await saveStylistConfig();
    } else {
      if (cloudConfig) {
        setIntensity(cloudConfig.intensity);
        setHairScale(cloudConfig.hairScale);
      }
      // FIX #1: Clear from in-memory cache instead of localStorage
      delete offlineCacheRef.current[`esdras_offline_fit_${sessionId}`];
    }
    setShowConflict(false);
  };

  const saveStylistConfig = async () => {
    const config = {
      intensity,
      hairScale,
      styleId: styleData?.id,
      stylistFinalized: true,
      lastStylistUpdate: new Date().toISOString()
    };

    if (!isOnline) {
      // FIX #1: Cache to in-memory ref instead of localStorage
      offlineCacheRef.current[`esdras_offline_fit_${sessionId}`] = JSON.stringify(config);
      toast("Cached Offline", { icon: '💾' });
      return;
    }

    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'live_sessions', sessionId), {
        ...config,
        lastStylistUpdate: serverTimestamp()
      });
      // FIX #1: Clear from in-memory cache instead of localStorage
      delete offlineCacheRef.current[`esdras_offline_fit_${sessionId}`];
      toast.success("Sync Successful");
    } catch (e) {
      // FIX #1: Cache to in-memory ref instead of localStorage
      offlineCacheRef.current[`esdras_offline_fit_${sessionId}`] = JSON.stringify(config);
      toast.error("Cloud Sync Failed");
    } finally { setIsSaving(false); }
  };

  const generateConfirmationReport = () => {
    const canvas = document.querySelector('canvas');
    const screenshot = canvas ? canvas.toDataURL("image/png") : null;
    const reportId = Math.random().toString(36).substring(7).toUpperCase();

    const reportHtml = `
      <html>
        <body style="background:#000; color:white; font-family:'Montserrat',sans-serif; padding:40px; text-align:center;">
          <div style="border:1px solid ${GOLD}; padding:30px; border-radius:20px; max-width:600px; margin:auto;">
            <h1 style="color:${GOLD}; letter-spacing:4px; font-size:1.2rem;">BIOMETRIC CONFIRMATION</h1>
            <p style="font-size:0.6rem; opacity:0.5; margin-bottom:30px;">REF: ${reportId}</p>
            ${screenshot ? `<img src="${screenshot}" style="width:100%; border:1px solid rgba(184,134,11,0.5); border-radius:15px; margin-bottom:20px;" />` : ''}
            <div style="text-align:left; background:rgba(255,255,255,0.03); padding:20px; border-radius:10px;">
              <p style="margin:5px 0; font-size:0.8rem;"><strong style="color:${GOLD}">STYLE:</strong> ${styleData?.name || 'Custom'}</p>
              <p style="margin:5px 0; font-size:0.8rem;"><strong style="color:${GOLD}">VOLUME:</strong> ${Math.round(intensity * 100)}%</p>
              <p style="margin:5px 0; font-size:0.8rem;"><strong style="color:${GOLD}">SCALE:</strong> ${hairScale.toFixed(2)}x</p>
            </div>
          </div>
        </body>
      </html>
    `;
    const blob = new Blob([reportHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Esdras_Fit_${reportId}.html`;
    link.click();
    toast.success("Report Ready");
  };

  if (loading) return <div style={loaderStyle}>INITIALIZING ENGINE...</div>;

  return (
    <div style={pageContainer}>
      {showConflict && (
        <div style={modalOverlay}>
          <div style={conflictModal}>
            <h3 style={{color: GOLD, fontSize: '0.8rem', letterSpacing: '2px'}}>SYNC CONFLICT</h3>
            <p style={{fontSize: '0.6rem', opacity: 0.8}}>Offline changes detected. Which version should prevail?</p>
            <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
              <button onClick={() => resolveConflict(true)} style={btnSmallGold}>USE LOCAL</button>
              <button onClick={() => resolveConflict(false)} style={btnSmallOutline}>FETCH CLOUD</button>
            </div>
          </div>
        </div>
      )}

      {!isOnline && (
        <div style={offlineBanner}>● OFFLINE - CACHING ENABLED</div>
      )}

      {isAdmin && (
        <div style={adminHud}>
          <div style={hudHeader}>OVERRIDE</div>
          <button onClick={() => setAdminOverride(!adminOverride)} style={adminBtn(adminOverride)}>CONTROL</button>
          <button onClick={() => setShowWireframe(!showWireframe)} style={adminBtn(showWireframe)}>WIRE</button>
        </div>
      )}

      <div style={header}>
        <button onClick={() => navigate(-1)} style={backCircle}>✕</button>
        <div style={titleBox}>
          <h2 style={styleName}>{styleData?.name?.toUpperCase() || '3D FIT'}</h2>
          <p style={styleSub}>{isMaster ? 'STYLIST MASTER' : 'CLIENT VIEW'}</p>
        </div>
        <div style={{display:'flex', gap:'10px'}}>
          {isMaster && sessionId && (
            <>
              <button onClick={saveStylistConfig} disabled={isSaving} style={btnSmallGold}>SAVE</button>
              <button onClick={generateConfirmationReport} style={btnSmallOutline}>REPORT</button>
            </>
          )}
          <button onClick={() => navigate('/dashboard')} style={btnSmallGold}>EXIT</button>
        </div>
      </div>

      <div style={{ height: '60%', width: '100%' }}>
        <ThreeErrorBoundary>
          <Canvas gl={{ preserveDrawingBuffer: true, antialias: true }} camera={{ position: [0, 0, 4] }}>
            <Suspense fallback={null}>
              {styleData && (
                <AvatarScene
                  hairUrl={styleData?.image || styleData?.imageUrl}
                  headUrl={userData?.headScan || userData?.photoURL}
                  intensity={intensity}
                  hairScale={hairScale}
                  wireframe={showWireframe}
                />
              )}
              <Environment preset="city" />
              <SyncedControls sessionId={sessionId} isMaster={isMaster} isAdminOverride={adminOverride} />
              {isAdmin && <Stats className="admin-stats" />}
            </Suspense>
          </Canvas>
        </ThreeErrorBoundary>
      </div>

      <div style={controlsContainer}>
        <div style={tabBar}>
          <button onClick={() => setActiveTab('vault')} style={activeTab === 'vault' ? activeTabStyle : inactiveTabStyle}>VAULT</button>
          <button onClick={() => setActiveTab('premium')} style={activeTab === 'premium' ? activePremiumTabStyle : inactiveTabStyle}>MARKET</button>
        </div>

        <div style={textureGrid}>
          {activeTab === 'vault' && personalVault.length > 0 ? (
            personalVault.map(s => (
              <div key={s.id} onClick={() => setStyleData(s)} style={{
                ...itemCard,
                border: styleData?.id === s.id ? `2px solid ${GOLD}` : '1px solid rgba(255,255,255,0.1)'
              }}>
                <img src={s.image} style={previewImg} alt="vault-item" />
              </div>
            ))
          ) : <p style={emptyText}>ASSETS READY</p>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '10px' }}>
          <div>
            <div style={labelRow}><span style={sliderLabel}>VOLUME</span><span style={sliderLabel}>{Math.round(intensity * 100)}%</span></div>
            <input type="range" min="0" max="1" step="0.01" value={intensity} onChange={e => setIntensity(parseFloat(e.target.value))} style={slider} />
          </div>
          {isMaster && (
            <div>
              <div style={labelRow}><span style={sliderLabel}>SCALE</span><span style={sliderLabel}>{hairScale.toFixed(2)}x</span></div>
              <input type="range" min="0.8" max="1.2" step="0.01" value={hairScale} onChange={e => setHairScale(parseFloat(e.target.value))} style={slider} />
            </div>
          )}
        </div>

        <div style={infoCard}>
          <div style={detailItem}><span style={label}>STATUS</span><span style={val}>{isOnline ? 'LIVE' : 'CACHED'}</span></div>
          <div style={divider}></div>
          <div style={detailItem}><span style={label}>ID</span><span style={val}>{sessionId ? sessionId.slice(0,6) : 'LOCAL'}</span></div>
        </div>
      </div>
    </div>
  );
}

// --- STYLES ---
const modalOverlay = { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 200 };
const conflictModal = { background: '#050a0f', border: `1px solid ${GOLD}`, padding: '30px', borderRadius: '20px', textAlign: 'center', maxWidth: '80%' };
const offlineBanner = { position: 'absolute', top: '90px', left: '50%', transform: 'translateX(-50%)', background: '#ff4b4b', color: 'white', padding: '5px 15px', borderRadius: '50px', fontSize: '0.6rem', fontWeight: 'bold', zIndex: 40 };
const errorOverlay = { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: '#000', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 10, color: GOLD, gap: '20px' };
const pageContainer = { height: '100vh', background: '#000', position: 'relative', overflow: 'hidden', fontFamily: 'Montserrat, sans-serif' };
const header = { position: 'absolute', top: 0, width: '100%', padding: '30px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 30, background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)' };
const backCircle = { width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer', display: 'grid', placeItems: 'center' };
const titleBox = { textAlign: 'center' };
const styleName = { color: GOLD, margin: 0, fontSize: '0.9rem', letterSpacing: '2px', fontWeight: '900' };
const styleSub = { color: '#fff', margin: 0, fontSize: '0.5rem', opacity: 0.5 };
const btnSmallGold = { background: GOLD, color: 'black', border: 'none', padding: '10px 18px', borderRadius: '50px', fontWeight: 'bold', fontSize: '0.6rem', cursor: 'pointer' };
const btnSmallOutline = { background: 'transparent', color: GOLD, border: `1px solid ${GOLD}`, padding: '10px 18px', borderRadius: '50px', fontWeight: 'bold', fontSize: '0.6rem', cursor: 'pointer' };
const controlsContainer = { position: 'absolute', bottom: 0, width: '100%', height: '40%', background: 'rgba(5, 10, 15, 0.98)', borderTop: `1px solid ${GOLD}`, padding: '25px', borderTopLeftRadius: '30px', borderTopRightRadius: '30px', zIndex: 20, boxSizing: 'border-box' };
const tabBar = { display: 'flex', gap: '8px', marginBottom: '20px', background: 'rgba(255,255,255,0.05)', padding: '5px', borderRadius: '15px' };
const activeTabStyle = { flex: 1, background: '#fff', color: '#000', border: 'none', padding: '10px', borderRadius: '10px', fontWeight: 'bold', fontSize: '0.65rem' };
const activePremiumTabStyle = { flex: 1, background: GOLD, color: '#000', border: 'none', padding: '10px', borderRadius: '10px', fontWeight: 'bold', fontSize: '0.65rem' };
const inactiveTabStyle = { flex: 1, background: 'transparent', color: '#fff', border: 'none', padding: '10px', fontSize: '0.65rem', opacity: 0.6 };
const sliderLabel = { color: GOLD, fontSize: '0.6rem', letterSpacing: '2px', fontWeight: 'bold' };
const labelRow = { display: 'flex', justifyContent: 'space-between' };
const slider = { width: '100%', accentColor: GOLD, marginTop: '8px' };
const textureGrid = { display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '15px' };
const itemCard = { minWidth: '85px', height: '85px', borderRadius: '15px', overflow: 'hidden', cursor: 'pointer', flexShrink: 0 };
const previewImg = { width: '100%', height: '100%', objectFit: 'cover' };
const infoCard = { marginTop: '20px', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '15px', padding: '15px', display: 'flex', justifyContent: 'space-around', alignItems: 'center' };
const detailItem = { display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'center' };
const label = { fontSize: '0.55rem', color: GOLD, fontWeight: 'bold' };
const val = { fontSize: '0.75rem', color: '#fff', fontWeight: '800' };
const divider = { width: '1px', height: '20px', background: 'rgba(184, 134, 11, 0.3)' };
const emptyText = { fontSize: '0.7rem', opacity: 0.5, textAlign: 'center', width: '100%', color: '#fff' };
const loaderStyle = { height: '100vh', background: '#000', color: GOLD, display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '0.7rem' };
const adminHud = { position: 'absolute', top: '100px', left: '20px', background: 'rgba(0,0,0,0.9)', border: '1px solid #ff0000', padding: '10px', zIndex: 100, borderRadius: '10px' };
const hudHeader = { color: '#ff0000', fontSize: '0.5rem', fontWeight: 'bold', marginBottom: '5px' };
const adminBtn = (active) => ({ background: active ? '#ff0000' : 'transparent', border: '1px solid #ff0000', color: active ? '#000' : '#ff0000', padding: '4px 6px', fontSize: '0.5rem', cursor: 'pointer', marginRight: '5px' });
